import { useEffect } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, describeVideoSessionError, resolveVideoSession } from '@astro/api'
import { useAuth } from '@astro/auth'
import { LuUserRound } from 'react-icons/lu'
import { unwrapBookingRecord } from '../components/sections/bookingDetails/bookingDetailsData'
import { BackButton, CallShell, useMediaPermission } from '../components/sections/videoCall/consultationShared'
import VideosdkConsultationRoom from '../components/sections/videoCall/VideosdkConsultationRoom'
import LiveKitConsultationRoom from '../components/sections/videoCall/LiveKitConsultationRoom'

/**
 * The consultation room — both parties land here. The astrologer (host)
 * arrives from "Start Video Consultation" (booking PATCHed to ready_to_start);
 * the seeker arrives by accepting the socket invite or the Join button. On the
 * seeker's first join the booking is PATCHed to in_progress. A countdown
 * seeded from the booking's duration auto-ends the call for everyone and
 * returns each side to the page they started from.
 *
 * The room's credentials come from GET /bookings/:id/call/join: the server
 * decides the role (the booking's astrologer is the host, its customer the
 * seeker), locks the booking to the admin's chosen video provider the first
 * time either side opens the call, and hands back the matching token. This
 * page only gates access and mounts the room for whichever provider that is
 * — VideosdkConsultationRoom or LiveKitConsultationRoom, both rendering the
 * same ConsultationRoomView.
 */
export default function VideoConsultationPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { status: authStatus, user } = useAuth()

  useEffect(() => {
    if (authStatus === 'unauthenticated') {
      navigate(`/login?redirect=${encodeURIComponent(`/consultations/${id}/video`)}`)
    }
  }, [authStatus, id, navigate])

  const { data, isPending, isError } = useQuery({
    queryKey: ['booking-details', id],
    queryFn: () => api.bookings.byId(id),
    enabled: Boolean(id) && authStatus === 'authenticated',
    retry: false,
  })
  const booking = data?.status === false ? null : unwrapBookingRecord(data)

  // Who we are on this booking. The seeker owns it; anyone else on this page
  // is the host. This is the PRE-join guess used for gating copy — the server
  // signs the real role into the join payload below, and that wins once known.
  const guessedRole = booking && user && String(booking.user_id) === String(user.id) ? 'seeker' : 'host'

  // The seeker can only enter once the astrologer has started the session.
  // A live socket invite also admits them (ready_to_start / in_progress are
  // what the astrologer's Start and the seeker's first join stamp, but a
  // lost PATCH must not lock a seeker out of a room that is open).
  const bookingStatus = String(booking?.status || '').toLowerCase()
  // Router state covers in-app acceptance; ?invited=1 covers cross-app
  // handoffs (website/shop invite watchers navigate with a full page load,
  // which cannot carry router state); the sessionStorage flag covers
  // REJOINING a still-live session the seeker left (set on join, cleared
  // when the astrologer ends the call or the timer runs out).
  const invited =
    Boolean(location.state?.invited) ||
    searchParams.get('invited') === '1' ||
    sessionStorage.getItem(`live-consultation-${id}`) === '1'
  const seekerAllowed = invited || ['ready_to_start', 'in_progress'].includes(bookingStatus)

  // Room credentials, on whichever provider the booking is locked to. Long
  // staleTime: a token lasts hours and a re-fetch would only re-mint it.
  const { data: joinData, error: joinError, isError: joinFailed } = useQuery({
    queryKey: ['consultation-call', id],
    queryFn: () => api.bookings.joinCall(id),
    enabled:
      authStatus === 'authenticated' &&
      Boolean(booking) &&
      (guessedRole === 'host' || seekerAllowed),
    staleTime: 60 * 60 * 1000,
    retry: 1,
  })
  const resolved = joinData ? resolveVideoSession(joinData, user) : null
  const session = resolved?.ok ? resolved.session : null

  const role = session ? (session.isHost ? 'host' : 'seeker') : guessedRole
  const backPath = role === 'host' ? `/astrologer/appointments/${id}` : `/bookings/${id}`

  // Audio-only sessions (consultation_type "phone" — the booking enum for the
  // astrologer's call mode): both ends join with the camera off, the video
  // toggle stays disabled, and the stage shows an audio-session notice. The
  // server says so on the payload; the booking's type is the fallback.
  const audioOnly = session
    ? Boolean(session.audioOnly)
    : /phone|call|audio/.test(String(booking?.consultation_type || '').toLowerCase())

  // The seeker's room joins itself, so the camera is acquired with no user
  // gesture in hand — and typically on an origin the browser has never prompted
  // for, because the invite popup in the website/shop hands the seeker over
  // with a full page load. Both SDKs join immediately and publish whatever they
  // have: if the prompt is still open, that is a mic-only session with no
  // webcam track. Settling the permission BEFORE the room mounts makes the
  // join publish.
  const devicesReady = useMediaPermission(role === 'seeker' && Boolean(session), !audioOnly)

  if (isPending || authStatus === 'loading') {
    return <CallShell><p className="text-white/60 text-sm animate-pulse">Preparing your consultation room…</p></CallShell>
  }

  if (isError || !booking) {
    return (
      <CallShell>
        <p className="font-cinzel text-xl text-white mb-2">Couldn&rsquo;t load this consultation</p>
        <p className="text-sm text-white/50 mb-6">Booking BKNG{id} may belong to another account.</p>
        <BackButton onClick={() => navigate(backPath)} label="Go Back" />
      </CallShell>
    )
  }

  if (role === 'seeker' && !seekerAllowed) {
    return (
      <CallShell>
        <LuUserRound className="w-12 h-12 mb-4 text-[#E6C878]/50" />
        <p className="font-cinzel text-xl text-white mb-2">The astrologer hasn&rsquo;t started the consultation yet</p>
        <p className="text-sm text-white/50 mb-6">You&rsquo;ll get an invitation the moment they open the room.</p>
        <BackButton onClick={() => navigate(backPath)} label="Back to Details" />
      </CallShell>
    )
  }

  if (joinFailed || (resolved && !resolved.ok)) {
    // The server's own message is the useful part: "This consultation belongs
    // to another account.", "Unable to create VideoSDK meeting.", "Live class
    // video is not configured." — each points at a different fix.
    const detail = joinFailed
      ? (joinError?.error?.message || joinError?.message || 'Please check your connection and try again.')
      : describeVideoSessionError(resolved.error, { isHost: role === 'host' })
    return (
      <CallShell>
        <p className="font-cinzel text-xl text-white mb-2">Couldn&rsquo;t open the consultation room</p>
        <p className="text-sm text-white/50 mb-6 max-w-md">{detail}</p>
        <BackButton onClick={() => navigate(backPath)} label="Back to Details" />
      </CallShell>
    )
  }

  if (!session || !devicesReady) {
    return <CallShell><p className="text-white/60 text-sm animate-pulse">Securing your meeting room…</p></CallShell>
  }

  const roomProps = {
    session,
    booking,
    role,
    backPath,
    audioOnly,
    // Host only: ask the server to close the room for everyone (what
    // disconnects a seeker still in a LiveKit room). Best-effort.
    onEndCall: role === 'host' ? () => api.bookings.endCall(booking.id).catch(() => {}) : undefined,
  }

  return session.provider === 'livekit'
    ? <LiveKitConsultationRoom {...roomProps} />
    : <VideosdkConsultationRoom {...roomProps} />
}
